import { faro } from "@grafana/faro-react"
import { Span, SpanStatusCode } from "@opentelemetry/api"
import extractFiles from "extract-files/extractFiles.mjs"
import isExtractableFile from "extract-files/isExtractableFile.mjs"
import { ExecutionResult, GraphQLFormattedError } from "graphql"
import { createClient as createClientSse, Sink as GraphQLSink, RequestParams } from "graphql-sse"
import {
  CacheConfig,
  ConcreteRequest,
  Environment,
  FetchFunction,
  GraphQLResponse,
  Network,
  Observable,
  RecordSource,
  RequestParameters as RelayRequestParameters,
  Variables as RelayVariables,
  Store,
} from "relay-runtime"
import { Sink } from "relay-runtime/network/RelayObservable"
import releaseInfo from "@/../release-info.json"
import { AzureAdLoginRedirect, performLogin } from "@/apis/bff"
import { createGraphQLClientHeaders } from "@/nitro-client-headers"
import { SofiaEnvironment } from "@/sofia-environment"
import { queriesStore } from "@/stores/queries-store"

const ErrorMessages = {
  FAILED_FETCH: "Failed to fetch",
  ERROR_FETCH: "Error in fetch",
  UNWORKABLE_FETCH: "Unworkable fetch",
  SOCKET_CLOSED: "Socket closed",
  GRAPHQL_ERRORS: "GraphQL error",
}

class NetworkError extends Error {
  constructor(message: string, options: ErrorOptions) {
    super(message, options)

    this.name = "NetworkError"
  }
}

const sleep = (delay: number) => new Promise<void>((resolve) => setTimeout(resolve, delay))

const getJitterValue = (min: number, max: number) => Math.floor(Math.random() * (max - min) + min)

const GRAPHQL_CLIENT_HEADERS = createGraphQLClientHeaders(releaseInfo.imageTag)

const GRAPHQL_JSON_HEADERS = {
  Accept: "application/graphql-response+json; charset=utf-8, application/json; charset=utf-8",
  "Content-Type": "application/json",
  "X-CSRF": "1",
  ...GRAPHQL_CLIENT_HEADERS,
}

const buildOperationUrl = (params: RelayRequestParameters, usePersistedRoute: boolean) =>
  usePersistedRoute
    ? `${SofiaEnvironment.Bff.GraphqlEndpoint}persisted/${params.id}/${params.name}`
    : `${SofiaEnvironment.Bff.GraphqlEndpoint}?op=${params.name}`

const buildOperationBody = (params: RelayRequestParameters, variables: RelayVariables, usePersistedRoute: boolean) =>
  usePersistedRoute ? { variables } : { operationName: params.name, query: params.text, id: params.id, variables }

const readGraphQlErrorCode = (graphQlError: GraphQLFormattedError) => {
  const extensionCode = graphQlError.extensions?.["code"]
  if (typeof extensionCode === "string") {
    return extensionCode
  }

  if ("__typename" in graphQlError && typeof graphQlError.__typename === "string") {
    return graphQlError.__typename
  }

  return "unknown"
}

const reportGraphQlOperationFailure = (
  span: Span | undefined,
  request: RelayRequestParameters,
  errors: readonly GraphQLFormattedError[],
) => {
  span?.setStatus({ code: SpanStatusCode.ERROR })
  span?.setAttribute("graphql.errors", JSON.stringify(errors))

  if (!faro?.api || faro.config.paused) {
    return
  }

  faro.api.pushError(new Error(errors[0]?.message ?? "GraphQL operation failed"), {
    type: "graphql_error",
    context: {
      operationName: request.name || "unnamed",
      operationId: request.id || "unknown",
      errorCodes: Array.from(new Set(errors.map(readGraphQlErrorCode))).join(","),
      errorCount: String(errors.length),
    },
    spanContext: span?.spanContext(),
  })
}

// If the application itself aborts a request it should either report the triggering error itself or
// (more likely) it is not an error. In any case, we filter those cases here.
const wasOperationStoppedByApplication = (cacheConfig: CacheConfig, error: unknown) => {
  // If we redirect to Entra, we are fine with not reporting it as an error.
  if (error instanceof AzureAdLoginRedirect) {
    return true
  }

  // Checking that the error is actually the abort reason ensures we don't skip reporting other errors
  // that happen to be thrown after the abort.
  const abortSignal = cacheConfig.metadata?.["signal"]
  return abortSignal instanceof AbortSignal && abortSignal.aborted && error === abortSignal.reason
}

// A failure with no GraphQL response at all (dropped connection, non-JSON body), reported under the
// same type so one operation stays one series. The error name goes in the code slot.
const reportGraphQlTransportFailure = (span: Span | undefined, request: RelayRequestParameters, error: Error) => {
  span?.setStatus({ code: SpanStatusCode.ERROR })
  span?.recordException(error)

  if (!faro?.api || faro.config.paused) {
    return
  }

  faro.api.pushError(error, {
    type: "graphql_error",
    context: {
      operationName: request.name || "unnamed",
      operationId: request.id || "unknown",
      errorCodes: error.name,
      errorCount: "1",
    },
    spanContext: span?.spanContext(),
  })
}

const fetchGraphQL: FetchFunction = async (
  request: RelayRequestParameters,
  variables: RelayVariables,
  cacheConfig: CacheConfig,
) => {
  const otel = faro?.api?.getOTEL()
  const tracer = otel?.trace && otel?.context ? otel.trace.getTracer("relay-graphql") : undefined
  const span = tracer?.startSpan(`GraphQL: ${request.name || "unnamed"}`)
  const parentContext =
    span && otel?.trace && otel?.context ? otel.trace.setSpan(otel.context.active(), span) : undefined

  if (span) {
    span.setAttribute("graphql.operation.name", request.name || "unnamed")
    span.setAttribute("graphql.operation.id", request.id || "unknown")
  }

  // Extract core fetch logic for tracing
  const executeGraphQLFetch = async () => {
    const { clone: variablesClone, files } = extractFiles(variables, isExtractableFile, "variables")

    // Semantic persisted operation route: when Relay emits a persisted operation id, the operation is
    // addressed by id + name in the URL path and only variables are sent in the body. Multipart file
    // uploads are not supported by that route (it only accepts a JSON {variables, extensions} body), so
    // uploads and the non-persisted (dev) build fall back to the classic endpoint with the full request.
    const usePersistedRoute = Boolean(request.id) && files.size === 0
    const graphqlUrl = buildOperationUrl(request, usePersistedRoute)

    let requestInit: RequestInit | undefined = undefined // <-- the request init is different header between then normal fetch and the fetch with uploadables
    if (files.size > 0) {
      const formData = new FormData()
      formData.append(
        "operations",
        JSON.stringify({
          operationName: request.name, // <-- The GraphQL operation name
          query: request.text,
          id: request.id, // <-- Please don't remove: it is required for persisted queries, otherwise it is null
          variables: variablesClone,
        }),
      )

      // Map each multipart form field name to the variable paths it populates, per
      // the GraphQL multipart request spec:
      // https://github.com/jaydenseric/graphql-multipart-request-spec
      const pathMap: { [key: string]: Array<string> } = {}
      let fileIndex = 0
      files.forEach((paths) => {
        pathMap[++fileIndex] = paths
      })
      formData.append("map", JSON.stringify(pathMap))

      fileIndex = 0
      files.forEach((_paths, file) => {
        formData.append(`${++fileIndex}`, file) // <-- Add file content
      })

      requestInit = {
        method: "POST",
        credentials: SofiaEnvironment.Bff.Credentials,
        headers: {
          Accept: "application/graphql-response+json; charset=utf-8, application/json; charset=utf-8",
          "Graphql-Preflight": "1",
          "X-CSRF": "1",
          ...GRAPHQL_CLIENT_HEADERS,
        },
        body: formData,
      }
    } else {
      requestInit = {
        credentials: SofiaEnvironment.Bff.Credentials,
        method: "POST",
        headers: GRAPHQL_JSON_HEADERS,
        body: JSON.stringify(buildOperationBody(request, variables, usePersistedRoute)),
      }
    }
    if (cacheConfig.metadata?.signal) {
      Object.assign(requestInit, { signal: cacheConfig.metadata.signal })
    }

    // In this block we allow any type since the response could contain literally anything
    // type-coverage:ignore-next-line
    const shouldRetry = (
      resp: Response,
      data: any, // oxlint-disable-line @typescript-eslint/no-explicit-any
    ): boolean =>
      // type-coverage:ignore-next-line
      resp.status === 500 || data?.errors?.some((error: any) => error.message === "Internal Execution Error") // oxlint-disable-line @typescript-eslint/no-explicit-any

    let resp: Response
    // In this block we allow any type since the response could contain literally anything
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    let responseData: any
    let retries = 0

    do {
      if (retries > 0) {
        console.log(`Retrying failed request, attempt ${retries}`)
        await sleep(retries * 200 + getJitterValue(100, 500)) // linear backoff + jitter
      }

      const doFetch = () => fetch(graphqlUrl, requestInit)
      resp = parentContext && otel?.context ? await otel.context.with(parentContext, doFetch) : await doFetch()

      if (resp.status === 401) {
        performLogin()
        throw new AzureAdLoginRedirect()
      }

      let response
      try {
        response = await resp.json()
      } catch {
        throw new Error(`HTTP ${resp.status}: expected a GraphQL response but received non-JSON content`)
      }

      if (resp.status !== 500) {
        responseData = response
      }

      retries++
    } while (shouldRetry(resp, responseData) && retries <= 5)

    // A document can hold more than one FieldResult field, so collect every field's errors
    // A successful payload has `errors` set to null, so check the value too.
    const clientErrors: GraphQLFormattedError[] = []
    if (responseData?.data) {
      // type-coverage:ignore-next-line
      Object.values(responseData.data).forEach((mutationResult) => {
        if (mutationResult && typeof mutationResult === "object" && "errors" in mutationResult) {
          // type-coverage:ignore-next-line
          const mutationErrors: readonly GraphQLFormattedError[] = Array.isArray(mutationResult.errors)
            ? mutationResult.errors
            : []
          clientErrors.push(...mutationErrors)
        }
      })
    }
    // type-coverage:ignore-next-line
    const executionErrors: readonly GraphQLFormattedError[] | undefined = responseData?.errors

    const failedOperationErrors = [...(executionErrors ?? []), ...clientErrors]

    queriesStore.addQuery({
      id: request.id || "unknown",
      name: request.name || "unnamed",
      error: failedOperationErrors.length ? failedOperationErrors : undefined,
      text: request.text || "unknown",
      variables: variables,
    })

    if (failedOperationErrors.length) {
      console.log("GraphQL call errored", {
        Error: failedOperationErrors,
        Request: requestInit,
      })

      reportGraphQlOperationFailure(span, request, failedOperationErrors)
    }

    // Synthesize a GraphQL error response so Relay doesn't crash on undefined
    if (!responseData) {
      return { errors: [{ message: "All retries exhausted" }] } as GraphQLResponse
    }

    if (!failedOperationErrors.length) {
      span?.setStatus({ code: SpanStatusCode.OK })
    }

    return responseData
  }

  try {
    const result = await executeGraphQLFetch()
    return result
  } catch (error) {
    const operationStoppedByApplication = wasOperationStoppedByApplication(cacheConfig, error)
    const failedOperationError = error instanceof Error ? error : new Error(String(error))

    if (!operationStoppedByApplication) {
      reportGraphQlTransportFailure(span, request, failedOperationError)
    }

    queriesStore.addQuery({
      id: request.id || "unknown",
      name: request.name || "unnamed",
      error: failedOperationError,
      text: request.text || "unknown",
      variables: variables,
    })

    throw error
  } finally {
    span?.end()
  }
}

function createSubscriptionSink(
  sink: Sink<GraphQLResponse>,
  operation: RelayRequestParameters,
  variables: RelayVariables,
  cacheConfig: CacheConfig,
): GraphQLSink<ExecutionResult<GraphQLResponse, unknown>> {
  return {
    next: (response) => {
      console.debug(`Received GraphQL subscription message`, response)

      // Track subscription messages in queriesStore
      queriesStore.addQuery({
        id: operation.id || "unknown",
        name: operation.name || "unnamed",
        error: response?.errors,
        text: operation.text || "unknown",
        variables,
        data: cacheConfig.metadata?.sensitive ? "sensitive" : response,
      })

      sink.next(response as GraphQLResponse)
    },
    complete: sink.complete,
    error: (err: Error) => {
      console.debug("Received GraphQL subscription error", err)

      queriesStore.addQuery({
        id: operation.id || "unknown",
        name: operation.name || "unnamed",
        error: err,
        text: operation.text || "unknown",
        variables,
      })

      if (Array.isArray(err)) {
        return sink.error(
          new NetworkError(ErrorMessages.ERROR_FETCH, {
            cause: err,
          }),
        )
      }

      if (err instanceof CloseEvent) {
        return sink.error(
          new NetworkError(ErrorMessages.SOCKET_CLOSED, {
            cause: err,
          }),
        )
      }

      return sink.error(err, true)
    },
  }
}

function createSubscriptionUrl(operation: RelayRequestParameters) {
  return operation.id
    ? `${SofiaEnvironment.Bff.GraphqlEndpoint}persisted/${operation.id}/${operation.name}`
    : `${SofiaEnvironment.Bff.GraphqlEndpoint}?op=subscription`
}

function createSubscriptionPayload(operation: RelayRequestParameters, variables: RelayVariables) {
  return operation.id
    ? { variables }
    : {
        operationName: operation.name,
        query: operation.text,
        variables,
      }
}

function createSubscriptionClient(operation: RelayRequestParameters) {
  return createClientSse({
    credentials: SofiaEnvironment.Bff.Credentials,
    url: createSubscriptionUrl(operation),
    retryAttempts: Number.MAX_SAFE_INTEGER,
    retry: async () => {
      return await sleep(5000 + getJitterValue(300, 3000)) // add some jitter: random time from 300ms to 3s
    },
    headers: { "X-CSRF": "1", ...GRAPHQL_CLIENT_HEADERS },
  })
}

function sseSubscribe() {
  function subscribe(
    operation: RelayRequestParameters,
    variables: RelayVariables,
    cacheConfig: CacheConfig,
  ): Observable<GraphQLResponse> {
    console.debug("Subscribe for GraphQL subscription", operation)
    const client = createSubscriptionClient(operation)
    return Observable.create((sink) => {
      const unsubscribe = client.subscribe<GraphQLResponse>(
        createSubscriptionPayload(operation, variables) as RequestParams,
        createSubscriptionSink(sink, operation, variables, cacheConfig),
      )
      return () => {
        unsubscribe()
        client.dispose()
      }
    })
  }

  return subscribe
}

// Best-effort: survives a page unload (uses fetch keepalive), not a crash/kill.
const sendOperationBeforeUnload = (operation: ConcreteRequest, variables: RelayVariables): void => {
  const usePersistedRoute = Boolean(operation.params.id)

  void fetch(buildOperationUrl(operation.params, usePersistedRoute), {
    method: "POST",
    keepalive: true,
    credentials: SofiaEnvironment.Bff.Credentials,
    headers: GRAPHQL_JSON_HEADERS,
    body: JSON.stringify(buildOperationBody(operation.params, variables, usePersistedRoute)),
  }).catch(() => {})
}

function createRelayEnvironment() {
  const subscriptionMode = sseSubscribe()
  return new Environment({
    network: Network.create(fetchGraphQL, subscriptionMode),
    store: new Store(new RecordSource()),
  })
}

const RelayEnvironment = createRelayEnvironment()

export { RelayEnvironment, sendOperationBeforeUnload }
